import React from 'react'
import styled, { css } from 'styled-components'
import { Colors, Spacing } from 'cio-design-library/design-tokens'
import { hexToRgba } from 'builder/styles/colors'
import Media from 'builder/styles/media'

import Tabs, { Tab } from 'builder/components/Tabs'

import Typography, { FontWeights } from 'builder/styles/typography'
import Select from 'builder/components/Select'
import { ScrollContainer } from 'builder/components/ScrollContainer'

const hideScroll = css`
  -ms-overflow-style: none; // Internet Explorer 10+
  scrollbar-width: none; // Firefox
  &::-webkit-scrollbar {
    display: none; // Safari and Chrome
  }
`

export const Container = styled.div`
  width: 409px;
  flex-shrink: 0;
  border-right: 1px solid ${hexToRgba(Colors.White, 0.15)};
  display: flex;
  flex-direction: column;

  ${Media.Laptop`
    width: 265px;
  `}

  ${Media.Tablet`
    width: 241px;
  `}
`

export const CustomTabs = styled(Tabs)`
  border-bottom: 1px solid ${Colors.Neutral70};
  justify-content: space-between;
  gap: ${Spacing.L};
  width: fit-content;
`

export const TabContainer = styled.div`
  padding: 0 32px;
  overflow-x: scroll;
  ${hideScroll};
  min-height: 57px;
`

export const ContentContainer = styled.div`
  flex: 1 1 90%;
  overflow: hidden;
`

export const ContentScrollWrapper = styled(ScrollContainer)`
  height: 100%;
  overflow-y: auto;

  &.has-overflow-space {
    padding-bottom: ${Spacing['4XL']};
  }

  &::-webkit-scrollbar {
    width: 4px;
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${Colors.Neutral70};
    border: 4px solid transparent;
    border-radius: 4px;

    &:hover {
      background-color: ${Colors.Neutral100};
    }
  }
`

export const CustomTab = styled(Tab)<{ isActive: boolean }>`
  padding: ${Spacing.S} 0;
  margin-right: unset;
  color: ${({ isActive }) => (isActive ? Colors.White : Colors.Neutral50)};

  &:hover {
    color: ${Colors.Blue40};
  }
`

export const TabLabelContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${Spacing['2XS']};
  align-items: center;
  justify-content: center;
  color: inherit;
`

export const IconContainer = styled.div`
  width: 24px;
  height: 24px;
  color: inherit;
`

export const SelectContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: ${Spacing['3XS']};
  width: 100%;
`

export const SelectLabel = styled.p`
  color: ${Colors.Neutral50};
  ${Typography.Caption};
  ${FontWeights.Regular};
`

export const CustomSelect = styled(Select)`
  width: 100%;
  margin-bottom: unset;

  /* Custom scrollbar */
  scrollbar-color: ${Colors.Neutral30} ${Colors.Neutral60};
  scrollbar-width: thin;

  button {
    background-color: #454c5b;
    color: ${Colors.Neutral5};
    border: unset;

    &:hover {
      background-color: #454c5b;
    }

    svg {
      fill: ${Colors.Neutral5};
    }

    &:disabled {
      color: ${Colors.Neutral50};

      svg {
        fill: ${Colors.Neutral50};
      }
    }
  }

  div {
    color: ${Colors.Neutral5};
  }

  div[role='listbox'] {
    background-color: #454c5b;
    border-radius: ${Spacing['3XS']};
    text-align: left;
  }

  div[role='option'] {
    div {
      color: ${Colors.Neutral5};

      &:hover {
        background-color: ${Colors.Neutral70} !important;
        color: ${Colors.Neutral30} !important;
      }
    }

    &[aria-selected='true'] {
      div {
        color: ${Colors.Neutral40};
        background-color: #454c5b;
      }
    }

    &[aria-disabled='true'] {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
`

export const SliderContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 12px;
  width: 100%;

  ${Media.AboveTablet`
    align-items: flex-start;
  `};
`

export const SliderLabel = styled.p`
  color: ${Colors.Neutral50};
  ${Typography.Caps};
  ${FontWeights.DemiBold};
`

export const TabContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px ${Spacing.S} ${Spacing.XL} ${Spacing.S};
  gap: ${Spacing.L};

  ${Media.AboveTablet`
    padding: 32px;
  `};
`

export const GroupControlWrapper = styled.div<{ color?: keyof typeof Colors; spacing?: 'L' | 'M' }>`
  display: flex;
  flex-direction: column;
  gap: ${props => (props.spacing === 'L' ? '24px' : '16px')};
  color: ${({ color }) => (color ? Colors[color] : Colors.White)};
`

export const GroupControlTitle = styled.h2<{
  size?: keyof typeof Typography
  weight?: keyof typeof FontWeights
}>`
  ${props => (props.size && Typography[props.size]) || Typography.Caption};
  ${props => (props.weight && FontWeights[props.weight]) || FontWeights.DemiBold};
  letter-spacing: 1.8px;
  text-transform: uppercase;
`

export const GroupControlBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.L};
`

export const GroupControl = (props: {
  title: string
  spacing?: 'L' | 'M'
  color?: keyof typeof Colors
  size?: keyof typeof Typography
  weight?: keyof typeof FontWeights
  children: React.ReactNode
  className?: string
}) => {
  return (
    <GroupControlWrapper
      color={props.color}
      spacing={props.spacing || 'M'}
      className={props.className}
    >
      <GroupControlTitle size={props.size} weight={props.weight}>
        {props.title}
      </GroupControlTitle>
      <GroupControlBody>{props.children}</GroupControlBody>
    </GroupControlWrapper>
  )
}
